import EventIcon from "@mui/icons-material/Event";
import Box from "@mui/material/Box";
import {
  Create,
  CreateButton,
  Datagrid,
  DateInput,
  Edit,
  List,
  SimpleForm,
  TextField,
  TextInput,
  TopToolbar,
  FunctionField,
  type CreateProps,
  type EditProps,
  type ListProps,
} from "react-admin";
import CustomEmpty from "@/components/custom-empty";
import { DisplayDateField } from "@/components/DisplayDateField";
import { formatListDate } from "@/utils/date-format";
import { OfficeHolidaysImportButton } from "./OfficeHolidaysImportButton";

function OfficeHolidayForm() {
  return (
    <>
      <DateInput source="date" label="Date" isRequired fullWidth />
      <TextInput source="name" label="Holiday name" isRequired fullWidth />
      <TextInput source="notes" label="Notes" fullWidth multiline rows={3} />
    </>
  );
}

const ListActions = () => (
  <TopToolbar>
    <OfficeHolidaysImportButton />
    <CreateButton />
  </TopToolbar>
);

const emptyActions = (
  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
    <OfficeHolidaysImportButton />
    <CreateButton variant="contained" />
  </Box>
);

export function OfficeHolidaysList(props: ListProps) {
  return (
    <List
      {...props}
      sort={{ field: "date", order: "DESC" }}
      perPage={50}
      actions={<ListActions />}
      empty={
        <CustomEmpty
          title="No office holidays yet"
          subtitle="Create one, or download the template and import many from CSV or Excel."
          action={emptyActions}
        />
      }
    >
      <Datagrid rowClick="edit">
        <DisplayDateField source="date" label="Date" />
        <TextField source="name" label="Name" />
        <FunctionField
          label="Notes"
          render={(record: { notes?: string | null }) => record.notes?.trim() || "—"}
        />
      </Datagrid>
    </List>
  );
}

export function OfficeHolidaysCreate(props: CreateProps) {
  return (
    <Create {...props} redirect="list">
      <SimpleForm warnWhenUnsavedChanges sx={{ maxWidth: 500 }}>
        <OfficeHolidayForm />
      </SimpleForm>
    </Create>
  );
}

export function OfficeHolidaysEdit(props: EditProps) {
  return (
    <Edit {...props} mutationMode="pessimistic">
      <SimpleForm warnWhenUnsavedChanges sx={{ maxWidth: 500 }}>
        <FunctionField
          label="Current date"
          render={(record: { date?: string }) =>
            record?.date ? formatListDate(record.date, { empty: "—" }) : "—"
          }
        />
        <OfficeHolidayForm />
      </SimpleForm>
    </Edit>
  );
}

export default {
  list: OfficeHolidaysList,
  create: OfficeHolidaysCreate,
  edit: OfficeHolidaysEdit,
  icon: EventIcon,
  label: "Office holidays",
  options: { label: "Office holidays" },
  name: "officeHolidays",
};
